import { deepmerge } from '@mui/utils';
import type { ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { DEFAULTCOLOR_TEXT_DISABLED } from '../default/palette';
import type { Device } from '../default';
import { generateDefaultThemeOptions } from '../default';
import generateFontFaceDeclarations from '../../utils/generateFontFaceDeclarations';
import OttoSansThin from './fonts/OS-Thin-131212.woff';
import OttoSansRegular from './fonts/OS-Regular-131212.woff';
import OttoSansBold from './fonts/OS-Bold-131212.woff';
import KalamBold from './fonts/kalam-bold.woff';

const COLOR_PRIMARY_MAIN = '#d4021d';
const COLOR_SECONDARY_MAIN = '#ff0000';
const DEFAULTCOLOR_TEXT_DARK = '#222222';
const DEFAULTCOLOR_TEXT_LIGHT = '#ffffff';
const DEFAULTCOLOR_TEXT_TRANSPARENT_DARK = 'rgba(34, 34, 34, 0.6)';
const DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT = 'rgba(255, 255, 255, 0.6)';

const FONT_FAMILY = 'OttoSans, Arial, Helvetica, sans-serif';
export const FONTS = {
  OttoSans: {
    300: OttoSansThin,
    400: OttoSansRegular,
    700: OttoSansBold,
  },
  Kalam: {
    700: KalamBold,
  },
};
const FONTFACE_DECLARATIONS = generateFontFaceDeclarations(FONTS);

export const FONT_PRELOADS = [FONTS.OttoSans[300], FONTS.OttoSans[400], FONTS.OttoSans[700]];

/** Define theme object.
 * See here for more info: https://mui.com/customization/theming/ */
const themeOptions: ThemeOptions = {
  // https://mui.com/customization/palette/
  palette: {
    primary: {
      main: COLOR_PRIMARY_MAIN,
      textTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
      contrastText: DEFAULTCOLOR_TEXT_LIGHT,
    },
    secondary: {
      main: COLOR_SECONDARY_MAIN,
    },
    grey: {
      light: '#f6f6f6',
      main: '#e6e6e6',
      dark: '#777777',
    },
    text: {
      disabled: DEFAULTCOLOR_TEXT_DISABLED,
      primary: DEFAULTCOLOR_TEXT_DARK,
      secondary: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      dark: DEFAULTCOLOR_TEXT_DARK,
      darkTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_DARK,
      light: DEFAULTCOLOR_TEXT_LIGHT,
      lightTransparent: DEFAULTCOLOR_TEXT_TRANSPARENT_LIGHT,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightBold: 700,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: FONTFACE_DECLARATIONS,
    },
    MuiButton: {
      styleOverrides: {
        root: ({
          theme: {
            unstable_sx: sx,
            palette: { grey },
          },
        }) =>
          sx({
            ':disabled': { bgcolor: grey.main, color: grey.dark },
          }),
      },
    },
  },
};

/** Merge with default theme options and create a theme instance
 * See here for more info: https://mui.com/customization/theming/#api */
const theme = (device: Device) =>
  createTheme(deepmerge(generateDefaultThemeOptions(device), themeOptions));

// eslint-disable-next-line import/no-default-export
export default theme;
